import {
  isValidDateString,
  toDateString,
  toUTCDateString,
  type DateString,
} from '@repo/utils';
import type { AmountRangeValue } from '@repo/ui/components/molecules/filter-panel';
import {
  toPickerDate,
  type DateRangeValue,
} from '@repo/ui/lib/date-range-param';

/**
 * Valori del pannello Filtri (tavola 8a) ⇄ parametri d'URL e di API.
 *
 * Le liste della webapp tengono i filtri nell'URL con gli stessi nomi dei
 * parametri dell'API: un insieme di valori viaggia come CSV (`DRAFT,COUNTING`,
 * lo stesso formato che il client HTTP usa per gli array), un periodo come due
 * giorni di calendario `YYYY-MM-DD`, un importo come `amountMin`/`amountMax`.
 * Un filtro vuoto è sempre il parametro ASSENTE (`undefined`). Funzioni pure,
 * condivise da tutte le tabelle che adottano il pannello: ogni lista legge e
 * scrive l'URL allo stesso modo.
 */

/**
 * Intervallo di giorni nel form: `Date` a mezzanotte LOCALE (date picker).
 * Accetta anche `null`, il valore che un campo svuotato può lasciare.
 */
export interface PanelDateRange {
  from?: Date | null;
  to?: Date | null;
}

export const EMPTY_PERIOD: DateRangeValue = {
  from: undefined,
  to: undefined,
};

/**
 * CSV dell'URL (o array) → valori ammessi, nell'ordine di `allowed`. Un deep
 * link scritto a mano con un valore sconosciuto non arriva al backend (400).
 * `null` quando non resta nulla: il campo del pannello è vuoto.
 */
export function parseCsvParam<TValue extends string>(
  param: unknown,
  allowed: readonly TValue[]
): TValue[] | null {
  const raw = Array.isArray(param)
    ? param.map(String)
    : typeof param === 'string'
      ? param.split(',')
      : [];
  const picked = new Set(raw.map((value) => value.trim()));
  return toChipFormValue(allowed.filter((value) => picked.has(value)));
}

/** Valori del pannello → CSV dell'URL; l'insieme vuoto è il parametro assente. */
export function toCsvParam(
  values: readonly string[] | null | undefined
): string | undefined {
  return values && values.length > 0 ? values.join(',') : undefined;
}

/** Insieme vuoto nel form = `null` (campo vuoto, come `FilterChipGroupForm`). */
export function toChipFormValue<TValue extends string>(
  values: readonly TValue[]
): TValue[] | null {
  return values.length > 0 ? [...values] : null;
}

/** Un solo valore ammesso dall'URL, o `null` (deep link con valore inventato). */
export function parseEnumParam<TValue extends string>(
  param: unknown,
  allowed: readonly TValue[]
): TValue | null {
  return typeof param === 'string' &&
    (allowed as readonly string[]).includes(param)
    ? (param as TValue)
    : null;
}

/**
 * Un parametro come giorno di calendario, o `undefined`. Un ISO completo
 * tiene la sola parte data; una `Date` (se un reviver è arrivato prima) è un
 * valore date-only a mezzanotte UTC.
 */
function toSearchDay(param: unknown): DateString | undefined {
  const day =
    param instanceof Date
      ? Number.isNaN(param.getTime())
        ? undefined
        : toUTCDateString(param)
      : typeof param === 'string'
        ? param.split('T')[0]
        : undefined;
  return day && isValidDateString(day) ? day : undefined;
}

/**
 * Due giorni dell'URL → valore del selettore periodo (mezzanotte LOCALE,
 * `toPickerDate`). Un giorno non valido si scarta; un periodo a metà resta a
 * metà.
 */
export function toPeriodFormValue(
  fromParam: unknown,
  toParam: unknown
): DateRangeValue {
  const from = toSearchDay(fromParam);
  const to = toSearchDay(toParam);
  return {
    from: from ? toPickerDate(from) : undefined,
    to: to ? toPickerDate(to) : undefined,
  };
}

export interface PeriodParams {
  from: DateString | undefined;
  to: DateString | undefined;
}

function isValidDate(date: Date | null | undefined): date is Date {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/** Valore del selettore → due giorni di calendario (giorno LOCALE del picker). */
export function toPeriodParams(
  value: PanelDateRange | null | undefined
): PeriodParams {
  return {
    from: isValidDate(value?.from) ? toDateString(value.from) : undefined,
    to: isValidDate(value?.to) ? toDateString(value.to) : undefined,
  };
}

/** Periodo con almeno un estremo valido. */
export function hasPeriod(value: PanelDateRange | null | undefined): boolean {
  const { from, to } = toPeriodParams(value);
  return from !== undefined || to !== undefined;
}

/**
 * Gruppi della sezione «Fonte» → unione delle fonti da inviare, nell'ordine
 * dei gruppi. Nessun gruppo = nessun filtro.
 */
export function sourcesFromGroups<
  TGroup extends string,
  TSource extends string,
>(
  groups: readonly TGroup[] | null | undefined,
  groupSources: Readonly<Record<TGroup, readonly TSource[]>>
): TSource[] {
  return (groups ?? []).flatMap((group) => [...groupSources[group]]);
}

/**
 * Fonti dell'URL → gruppi accesi: un gruppo è acceso solo se TUTTE le sue
 * fonti sono presenti, così il pannello mostra esattamente ciò che si invia.
 */
export function groupsFromSources<
  TGroup extends string,
  TSource extends string,
>(
  sources: unknown,
  groupSources: Readonly<Record<TGroup, readonly TSource[]>>
): TGroup[] {
  const allSources = Object.values<readonly TSource[]>(groupSources).flat();
  const present = new Set<string>(parseCsvParam(sources, allSources) ?? []);
  return (Object.keys(groupSources) as TGroup[]).filter((group) =>
    groupSources[group].every((source) => present.has(source))
  );
}

export interface AmountParams {
  amountMin?: number;
  amountMax?: number;
}

/**
 * Unica regola per gli estremi invertiti: min > max si raddrizza. Il
 * pannello spegne Applica finché l'intervallo è incoerente
 * (`isApplyFiltersDisabled`), quindi qui arrivano solo i link scritti a mano:
 * il backend risponderebbe 400.
 */
export function normalizeAmountRange(
  range: AmountRangeValue | null | undefined
): AmountRangeValue | null {
  const min = range?.min;
  const max = range?.max;
  if (min === undefined && max === undefined) return null;
  if (min !== undefined && max !== undefined && min > max) {
    return { min: max, max: min };
  }
  return {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  };
}

/**
 * Parametri d'URL → intervallo del form (`null` senza estremi). Un estremo
 * non numerico o negativo si scarta; gli estremi invertiti si raddrizzano.
 */
export function parseAmountRange(params: {
  amountMin?: unknown;
  amountMax?: unknown;
}): AmountRangeValue | null {
  const min = parseAmountBound(params.amountMin);
  const max = parseAmountBound(params.amountMax);
  return normalizeAmountRange({
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  });
}

/** Intervallo del form → `amountMin`/`amountMax` (estremi raddrizzati). */
export function toAmountParams(
  range: AmountRangeValue | null | undefined
): AmountParams {
  const normalized = normalizeAmountRange(range);
  return {
    ...(normalized?.min !== undefined && { amountMin: normalized.min }),
    ...(normalized?.max !== undefined && { amountMax: normalized.max }),
  };
}

export function isAmountRangeActive(
  range: AmountRangeValue | null | undefined
): boolean {
  return range?.min !== undefined || range?.max !== undefined;
}

/**
 * Un estremo di «Importo» dell'URL (valore assoluto in euro): un numero
 * finito non negativo, anche come testo; tutto il resto è assente.
 */
export function parseAmountBound(value: unknown): number | undefined {
  const amount =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(amount) && amount >= 0 ? amount : undefined;
}
